import { FC, useContext, useEffect, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Pagination, Preloader } from '@smartshell/ui-kit';

import { EventLogsTable, EventsFilter, LogMobile } from 'components';
import { DEFAULT_EVENTS_TYPE_FILTER } from 'consts';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  GET_EVENT_LIST,
  IGetEventListResponse,
  IGetEventsRequest,
} from 'schemas';
import { ELogType, IEventsFilter } from 'types';

import s from './WorkShiftEvents.module.scss';

export const WorkShiftEvents: FC<{
  workShiftId: number;
}> = ({ workShiftId }) => {
  const { t } = useContext(LocalizationContext);
  const [filters, setFilters] = useState<IEventsFilter | null>(null);
  const size = useWindowResize();
  const [
    getEvents,
    { data: loadedEvents, loading: isEventsLoading, variables, called },
  ] = useLazyQuery<IGetEventListResponse, IGetEventsRequest>(GET_EVENT_LIST, {
    fetchPolicy: 'no-cache',
    variables: {
      first: 300,
      input: {
        work_shift_id: workShiftId,
      },
    },
  });

  useEffect(() => {
    if (!filters) {
      return;
    }

    const timerId = setTimeout(() => {
      getEvents({
        variables: {
          input: {
            ...variables?.input,
            q: filters?.q || undefined,
            types: filters?.types.length
              ? filters.types
              : DEFAULT_EVENTS_TYPE_FILTER,
          },
        },
      });
    }, 300);

    return () => clearTimeout(timerId);
  }, [getEvents, filters?.q, filters?.types]);

  const events = loadedEvents?.eventList.data ?? [];
  const paginationInfo = loadedEvents?.eventList.paginatorInfo ?? {
    lastPage: 1,
    currentPage: 1,
  };

  return (
    <div className={s.container}>
      <EventsFilter setFilters={setFilters} />
      <Preloader
        loading={isEventsLoading || !called}
        hasData={events.length > 0}
        noDataPlaceholder={t('logs.placeholder')}
      >
        {size.width < 960 ? (
          <LogMobile
            eventLogs={loadedEvents?.eventList?.data?.filter(
              ({ type }) => type !== ELogType.SETTINGS_UPDATED
            )}
          />
        ) : (
          <div className={s['events-container']}>
            <EventLogsTable eventLogs={loadedEvents?.eventList.data || []} />
          </div>
        )}

        {paginationInfo.lastPage > 1 && (
          <Pagination
            onChange={(page) => {
              if (page != paginationInfo.currentPage) {
                getEvents({ variables: { ...variables, page } });
              }
            }}
            {...paginationInfo}
            className={s['pagination']}
          />
        )}
      </Preloader>
    </div>
  );
};
